import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import {
  DOCUMENT_STATUSES,
  DOCUMENT_TYPES,
  PAGINATION,
  dec,
  formatDate,
  type Document,
} from '@fdt/shared';
import { Autocomplete, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataTable, type Column } from '../components/DataTable';
import { ExportButton } from '../components/ExportButton';
import { MoneyText } from '../components/MoneyText';
import { useList } from '../hooks/useList';
import { currentLocale } from '../i18n';
import { customersApi } from '../resources';
import { documentsApi } from '../resources/documents';

const TYPE_COLOUR: Record<Document['type'], 'primary' | 'secondary' | 'default'> = {
  sale: 'primary',
  return: 'secondary',
  exchange: 'default',
};

export function DocumentsPage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const navigate = useNavigate();

  const [customerId, setCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const filters = useMemo(
    () => ({
      customerId: customerId || undefined,
      type: type || undefined,
      status: status || undefined,
      from: from || undefined,
      to: to || undefined,
    }),
    [customerId, type, status, from, to],
  );

  const crud = useList('documents', documentsApi.list, { filters });

  const customers = useQuery({
    queryKey: ['customers', 'picker', customerSearch],
    queryFn: () =>
      customersApi.list({
        page: 1,
        pageSize: 50,
        search: customerSearch || undefined,
        sortBy: 'businessName',
      }),
  });

  const columns: Column<Document>[] = [
    {
      id: 'issuedOn',
      label: t('fields.date'),
      render: (row) => formatDate(`${row.issuedOn}T00:00:00Z`, locale),
    },
    {
      id: 'number',
      label: t('documents.number'),
      render: (row) => (
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {row.number}
        </Typography>
      ),
    },
    {
      id: 'type',
      label: t('fields.type'),
      render: (row) => (
        <Chip size="small" color={TYPE_COLOUR[row.type]} label={t(`documents.types.${row.type}`)} />
      ),
    },
    {
      id: 'customerName',
      label: t('fields.businessName'),
      render: (row) => `${row.customerCode} — ${row.customerName}`,
    },
    {
      id: 'netTotal',
      label: t('documents.net'),
      align: 'right',
      render: (row) => <MoneyText value={row.netTotal} variant="body2" />,
    },
    {
      id: 'vatTotal',
      label: t('documents.vat'),
      align: 'right',
      render: (row) => <MoneyText value={row.vatTotal} variant="body2" />,
    },
    {
      id: 'grossTotal',
      label: t('documents.gross'),
      align: 'right',
      render: (row) => <MoneyText value={row.grossTotal} variant="body2" sx={{ fontWeight: 600 }} />,
    },
    {
      id: 'paidTotal',
      label: t('documents.paid'),
      align: 'right',
      render: (row) => {
        const outstanding = dec(row.grossTotal).minus(dec(row.paidTotal));
        return (
          <Stack spacing={0} alignItems="flex-end">
            <MoneyText value={row.paidTotal} variant="body2" />
            {!outstanding.isZero() && row.status === 'active' && (
              <Typography variant="caption" color="warning.main">
                {t('documents.outstanding')}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      id: 'status',
      label: t('fields.status'),
      render: (row) =>
        row.status === 'cancelled' ? (
          <Chip size="small" color="error" variant="outlined" label={t('documents.cancelled')} />
        ) : (
          <Chip size="small" color="success" label={t('documents.active')} />
        ),
    },
  ];

  return (
    <DataTable
      title={t('nav.documents')}
      icon={ReceiptLongIcon}
      tone="neutral"
      columns={columns}
      query={crud.list}
      search={crud.search}
      onSearchChange={crud.setSearch}
      sortBy={crud.sortBy}
      sortDir={crud.sortDir}
      onSort={crud.toggleSort}
      page={crud.page}
      onPageChange={crud.setPage}
      pageSize={crud.pageSize}
      onPageSizeChange={crud.setPageSize}
      rowKey={(row) => row.id}
      onRowClick={(row) => navigate(`/documents/${row.id}`)}
      toolbar={
        <>
          <Autocomplete
            options={customers.data?.items ?? []}
            getOptionLabel={(option) => `${option.code} — ${option.businessName}`}
            filterOptions={(options) => options}
            onInputChange={(_, value) => setCustomerSearch(value)}
            onChange={(_, value) => setCustomerId(value?.id ?? '')}
            renderInput={(params) => (
              <TextField {...params} label={t('fields.businessName')} size="small" />
            )}
            sx={{ minWidth: 260 }}
          />
          <TextField
            select
            label={t('fields.type')}
            value={type}
            onChange={(event) => setType(event.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {DOCUMENT_TYPES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`documents.types.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('fields.status')}
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {DOCUMENT_STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`documents.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('routes.from')}
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: 165 }}
          />
          <TextField
            label={t('routes.to')}
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: 165 }}
          />
          <ExportButton file="documents.xlsx" params={filters} />
        </>
      }
    />
  );
}

/** Kept so the page can be reached with the shared page-size cap in view. */
export const DOCUMENTS_PAGE_SIZE = PAGINATION.defaultPageSize;
